import type { ReactNode } from 'react';
import type { StatusTone } from '@/constants/defaults';
import { getStatusMeta } from '@/constants/defaults';
import type { ReturnStatus } from '@/types';
import { cn } from '@/lib/cn';

const TONE_CLASSES: Record<StatusTone, string> = {
  pending: 'bg-warning-soft text-warning ring-warning/20',
  progress: 'bg-info-soft text-info ring-info/20',
  success: 'bg-success-soft text-success ring-success/20',
  danger: 'bg-danger-soft text-danger ring-danger/20',
  neutral: 'bg-surface-sunken text-ink-muted ring-line',
};

interface BadgeProps {
  tone?: StatusTone;
  children: ReactNode;
  className?: string;
}

/** Generic tone badge. */
export function Badge({ tone = 'neutral', children, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Status badge that maps a return status to its label + tone. */
export function StatusBadge({ status, className }: { status: ReturnStatus | string | null | undefined; className?: string }) {
  const meta = getStatusMeta(status);
  return (
    <Badge tone={meta.tone} className={className}>
      {meta.label}
    </Badge>
  );
}
